import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Button,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    CircularProgress,
    Stack,
    Tooltip,
    Grid,
	Badge,
	TextField,
	InputAdornment,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	TableSortLabel,
	Card,
	CardContent
} from '@mui/material';
import {
    Visibility,
    CheckCircle,
    Warning,
    Error,
    Person,
    Assessment,
	Refresh,
	Search,
	CalendarToday,
	Print
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { formatDate } from '../../utils/constants';
import apiClient from '../../api';
import { formatTarpCell, formatTarpTotal, sumTarps } from '../../utils/tarpMetrics';
import ReceiptAttachmentGallery from './ReceiptAttachmentGallery';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { getTotalWeeklyMiles, formatTotalWeeklyMiles, mileageRowTruckLabel } from '../../utils/weeklyChecklistMiles';
import { WeeklyChecklistPrintDialog } from './WeeklyChecklistPrint';

const WeeklyChecklistList = ({ refreshTrigger = 0 }) => {
    const { user } = useAuth();
    const [checklists, setChecklists] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedChecklist, setSelectedChecklist] = useState(null);
    const [printOpen, setPrintOpen] = useState(false);
    const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
	const [search, setSearch] = useState('');
	const [statusFilter, setStatusFilter] = useState('all');
	const [reviewedFilter, setReviewedFilter] = useState('all');
	const [orderBy, setOrderBy] = useState('weekStartDate');
	const [order, setOrder] = useState('desc');
	const [driverFilter, setDriverFilter] = useState('all');
	const [startDateFilter, setStartDateFilter] = useState(null);
	const [endDateFilter, setEndDateFilter] = useState(null);
	const [screenWidth, setScreenWidth] = useState(window.innerWidth);

	// Log screen size changes
	useEffect(() => {
		const handleResize = () => {
			const newWidth = window.innerWidth;
			setScreenWidth(newWidth);
			console.log('[WeeklyChecklistList] Screen resized:', {
				width: newWidth,
				height: window.innerHeight,
				isMobile: newWidth < 900,
				isDesktop: newWidth >= 900,
				breakpoint: newWidth < 600 ? 'xs' : newWidth < 900 ? 'sm' : newWidth < 1200 ? 'md' : newWidth < 1536 ? 'lg' : 'xl'
			});
		};

		console.log('[WeeklyChecklistList] Initial screen size:', {
			width: window.innerWidth,
			height: window.innerHeight,
			isMobile: window.innerWidth < 900,
			isDesktop: window.innerWidth >= 900
		});

		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	// Log when component renders
	useEffect(() => {
		console.log('[WeeklyChecklistList] Component rendered with:', {
			checklistCount: checklists.length,
			screenWidth,
			shouldShowMobile: screenWidth < 900,
			shouldShowDesktop: screenWidth >= 900
		});
	}, [checklists.length, screenWidth]);

	const handleClearFilters = () => {
		setSearch('');
		setStatusFilter('all');
		setReviewedFilter('all');
		setDriverFilter('all');
		setStartDateFilter(null);
		setEndDateFilter(null);
		setOrderBy('weekStartDate');
		setOrder('desc');
	};

	const isDefaultFilters = search === ''
		&& statusFilter === 'all'
		&& reviewedFilter === 'all'
		&& driverFilter === 'all'
		&& startDateFilter === null
		&& endDateFilter === null
		&& orderBy === 'weekStartDate'
		&& order === 'desc';

    const isAdmin = user?.role === 'admin' || user?.role === 'company_admin';

    const fetchWeeklyChecklists = async () => {
        try {
            setLoading(true);
            setError('');
            console.log('[WeeklyChecklistList] Fetching checklists...');
            const response = await apiClient.get('/forms/weekly-checklist');
            console.log('[WeeklyChecklistList] Received checklists:', response.data);
            setChecklists(response.data.data || []);
        } catch (error) {
            console.error('Error fetching weekly checklists:', error);
            setError('Failed to load weekly checklists. Please try refreshing the page.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchWeeklyChecklists();
    }, []);

    // Refresh when refreshTrigger prop changes
    useEffect(() => {
        if (refreshTrigger > 0) {
            console.log('[WeeklyChecklistList] Refresh triggered by parent');
            fetchWeeklyChecklists();
        }
    }, [refreshTrigger]);

    const getStatusColor = (status) => {
        switch (status) {
            case 'satisfactory':
                return 'success';
            case 'needs_attention':
                return 'warning';
            case 'critical':
                return 'error';
            default:
                return 'default';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'satisfactory':
                return <CheckCircle color="success" />;
            case 'needs_attention':
                return <Warning color="warning" />;
            case 'critical':
                return <Error color="error" />;
            default:
                return <Assessment />;
        }
    };

    const getInspectionStatusColor = (status) => {
        switch (status) {
            case 'pass':
                return 'success';
            case 'fail':
                return 'error';
            case 'needs_attention':
                return 'warning';
            default:
                return 'default';
        }
    };

    const calculateMilesDriven = (startMileage, endMileage) => {
        return (endMileage - startMileage).toFixed(1);
    };

    const handleViewDetails = (checklist) => {
        setSelectedChecklist(checklist);
        setDetailsDialogOpen(true);
    };

    const handleMarkReviewed = async (checklist) => {
        if (!isAdmin) return;

        setSubmitting(true);
        setError('');

        try {
            await apiClient.put(`/forms/weekly-checklist/${checklist._id}`);
            
            // Refresh the list
            await fetchWeeklyChecklists();
        } catch (error) {
            console.error('Error marking checklist as reviewed:', error);
            setError(error.response?.data?.message || 'Failed to mark as reviewed');
        } finally {
            setSubmitting(false);
        }
    };

    const getIssuesCount = (checklist) => {
        const vehicleIssues = Object.values(checklist.vehicleInspection).filter(status => status === 'fail' || status === 'needs_attention').length;
        const safetyIssues = Object.values(checklist.safetyEquipment).filter(status => 
            status === 'missing' || status === 'expired' || status === 'damaged' || status === 'insufficient'
        ).length;
        const complianceIssues = Object.values(checklist.safetyQuestions).filter(answer => !answer).length;
        
        return vehicleIssues + safetyIssues + complianceIssues;
    };

	const handleRequestSort = (property) => {
		const isAsc = orderBy === property && order === 'asc';
		setOrder(isAsc ? 'desc' : 'asc');
		setOrderBy(property);
	};

	const getComparator = (key) => {
		return (a, b) => {
			let aVal;
			let bVal;
			switch (key) {
				case 'weekStartDate':
					aVal = new Date(a.weekStartDate).getTime();
					bVal = new Date(b.weekStartDate).getTime();
					break;
				case 'truck':
					aVal = a.truck?.truckNumber || '';
					bVal = b.truck?.truckNumber || '';
					break;
				case 'driver':
					aVal = a.submittedBy?.name || '';
					bVal = b.submittedBy?.name || '';
					break;
				case 'miles':
					aVal = getTotalWeeklyMiles(a);
					bVal = getTotalWeeklyMiles(b);
					break;
				case 'status':
					aVal = a.overallStatus || '';
					bVal = b.overallStatus || '';
					break;
				case 'issues':
					aVal = getIssuesCount(a);
					bVal = getIssuesCount(b);
					break;
				case 'reviewed':
					aVal = Boolean(a.reviewedBy) ? 1 : 0;
					bVal = Boolean(b.reviewedBy) ? 1 : 0;
					break;
				default:
					aVal = a[key];
					bVal = b[key];
			}
			if (aVal < bVal) return order === 'asc' ? -1 : 1;
			if (aVal > bVal) return order === 'asc' ? 1 : -1;
			return 0;
		};
	};

	// Truck label: backend populates unitNumber (not truckNumber); fallback to make+model
	const getTruckLabel = (truck) => {
		if (!truck) return 'N/A';
		const unit = truck.truckNumber || truck.unitNumber;
		const makeModel = [truck.make, truck.model].filter(Boolean).join(' ').trim();
		return unit || makeModel || 'N/A';
	};

	const filteredAndSorted = React.useMemo(() => {
		const lower = search.trim().toLowerCase();
		let rows = checklists.filter((c) => {
			const matchesSearch = !lower || [
				c.truck?.truckNumber,
				c.truck?.unitNumber,
				c.truck?.make,
				c.truck?.model,
				c.submittedBy?.name
			].filter(Boolean).some(v => String(v).toLowerCase().includes(lower));
			const matchesStatus = statusFilter === 'all' || c.overallStatus === statusFilter;
			const matchesReviewed = reviewedFilter === 'all' || (reviewedFilter === 'reviewed' ? Boolean(c.reviewedBy) : !c.reviewedBy);
			const matchesDriver = driverFilter === 'all' || (c.submittedBy?.name === driverFilter);
			const wsd = new Date(c.weekStartDate);
			const withinStart = !startDateFilter || wsd >= new Date(startDateFilter);
			const withinEnd = !endDateFilter || wsd <= new Date(endDateFilter);
			return matchesSearch && matchesStatus && matchesReviewed && matchesDriver && withinStart && withinEnd;
		});
		rows.sort(getComparator(orderBy));
		return rows;
	}, [checklists, search, statusFilter, reviewedFilter, driverFilter, startDateFilter, endDateFilter, orderBy, order]);

	const uniqueDrivers = React.useMemo(() => {
		const names = new Set(checklists.map(c => c.submittedBy?.name).filter(Boolean));
		return Array.from(names).sort();
	}, [checklists]);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Loading weekly checklists...</Typography>
            </Box>
        );
    }

    if (checklists.length === 0) {
        return (
            <Paper sx={{ p: 3 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6">
                        Weekly Checklists
                    </Typography>
                    <Button
                        startIcon={<Refresh />}
                        onClick={fetchWeeklyChecklists}
                        disabled={loading}
                        size="small"
                        variant="outlined"
                    >
                        Refresh
                    </Button>
                </Stack>
                
                {error ? (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                ) : (
                    <Alert severity="info">
                        No weekly checklists found. Submit your first checklist using the form above, and it will appear here for you to review.
                    </Alert>
                )}
            </Paper>
        );
    }



    return (
        <Box>
            <Paper sx={{ p: 3 }}>
		<Stack spacing={2} mb={2}>
			<Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ flexWrap: 'wrap', gap: 1 }}>
			<Typography variant="h6">
				Weekly Checklists ({filteredAndSorted.length}/{checklists.length})
			</Typography>
				<Button
					startIcon={<Refresh />}
					onClick={fetchWeeklyChecklists}
					disabled={loading}
					size="small"
					variant="outlined"
					sx={{ whiteSpace: 'nowrap' }}
				>
					Refresh
				</Button>
			</Stack>
			
			{/* Filters */}
			<Stack spacing={1.5}>
				<TextField
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					size="small"
					placeholder="Search by truck, make/model, or driver"
					fullWidth
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<Search fontSize="small" />
							</InputAdornment>
						)
					}}
				/>
				<Grid container spacing={1}>
					<Grid item xs={6} sm={4} md={2}>
						<FormControl size="small" fullWidth>
					<InputLabel>Status</InputLabel>
					<Select label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
						<MenuItem value="all">All</MenuItem>
						<MenuItem value="satisfactory">Satisfactory</MenuItem>
						<MenuItem value="needs_attention">Needs Attention</MenuItem>
						<MenuItem value="critical">Critical</MenuItem>
					</Select>
				</FormControl>
					</Grid>
					<Grid item xs={6} sm={4} md={2}>
						<FormControl size="small" fullWidth>
					<InputLabel>Reviewed</InputLabel>
					<Select label="Reviewed" value={reviewedFilter} onChange={(e) => setReviewedFilter(e.target.value)}>
						<MenuItem value="all">All</MenuItem>
						<MenuItem value="reviewed">Reviewed</MenuItem>
						<MenuItem value="pending">Pending Review</MenuItem>
					</Select>
				</FormControl>
					</Grid>
					<Grid item xs={12} sm={4} md={2}>
						<FormControl size="small" fullWidth>
					<InputLabel>Driver</InputLabel>
					<Select label="Driver" value={driverFilter} onChange={(e) => setDriverFilter(e.target.value)}>
						<MenuItem value="all">All</MenuItem>
						{uniqueDrivers.map(name => (
							<MenuItem key={name} value={name}>{name}</MenuItem>
						))}
					</Select>
				</FormControl>
					</Grid>
					<Grid item xs={6} sm={6} md={3}>
				<DatePicker
					label="Week start from"
					value={startDateFilter}
					onChange={(v) => setStartDateFilter(v)}
							sx={{ width: '100%' }}
							slotProps={{ textField: { size: 'small', fullWidth: true } }}
				/>
					</Grid>
					<Grid item xs={6} sm={6} md={3}>
				<DatePicker
					label="Week start to"
					value={endDateFilter}
					onChange={(v) => setEndDateFilter(v)}
							sx={{ width: '100%' }}
							slotProps={{ textField: { size: 'small', fullWidth: true } }}
				/>
					</Grid>
				</Grid>
				<Button
					onClick={handleClearFilters}
					disabled={isDefaultFilters}
					size="small"
					variant="text"
					color="inherit"
					sx={{ alignSelf: 'flex-start' }}
				>
					Clear All Filters
				</Button>
			</Stack>
		</Stack>
                
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {/* Mobile card view */}
                <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                    {console.log('[WeeklyChecklistList] RENDERING MOBILE CARD VIEW', { count: filteredAndSorted.length, screenWidth })}
                    <Stack spacing={2}>
                        {filteredAndSorted.map((checklist) => {
                            const issuesCount = getIssuesCount(checklist);
                            const milesDriven = formatTotalWeeklyMiles(checklist);
                            
                            return (
                                <Card key={checklist._id}>
                                    <CardContent>
                                        <Stack spacing={2}>
                                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                                <Stack spacing={0.5}>
                                                    <Typography variant="subtitle1" fontWeight="bold">
                                                        {getTruckLabel(checklist.truck)}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {checklist.truck?.make} {checklist.truck?.model}
                                                    </Typography>
                                                </Stack>
                                                <Chip
                                                    label={checklist.overallStatus.replace('_', ' ')}
                                                    color={getStatusColor(checklist.overallStatus)}
                                                    size="small"
                                                    icon={getStatusIcon(checklist.overallStatus)}
                                                />
                                            </Stack>
                                            
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                <Person fontSize="small" />
                                                <Typography variant="body2">
                                                    {checklist.submittedBy?.name || 'Unknown'}
                                                </Typography>
                                            </Stack>
                                            
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                <CalendarToday fontSize="small" />
                                                <Typography variant="body2">
                                                    {formatDate(checklist.weekStartDate)} - {formatDate(checklist.weekEndDate)}
                                                </Typography>
                                            </Stack>
                                            
                                            <Grid container spacing={1}>
                                                <Grid item xs={6}>
                                                    <Typography variant="caption" color="text.secondary">Miles</Typography>
                                                    <Typography variant="body2" fontWeight="medium">{milesDriven} mi</Typography>
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <Typography variant="caption" color="text.secondary">Fuel</Typography>
                                                    <Typography variant="body2" fontWeight="medium">${checklist.fuelPurchases?.toFixed(2) || '0.00'}</Typography>
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <Typography variant="caption" color="text.secondary">Issues</Typography>
                                                <Typography variant="body2" fontWeight="medium" component="div">
                                                        {issuesCount > 0 ? (
                                                            <Chip label={issuesCount} color="error" size="small" />
                                                        ) : (
                                                            <Chip label="None" color="success" size="small" />
                                                        )}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <Typography variant="caption" color="text.secondary">Status</Typography>
                                                    <Typography variant="body2" fontWeight="medium" component="div">
                                                        {checklist.reviewedBy ? (
                                                            <Chip label="Reviewed" color="success" size="small" icon={<CheckCircle />} />
                                                        ) : (
                                                            <Chip label="Pending" color="warning" size="small" />
                                                        )}
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                            
                                            {checklist.reviewedBy && (
                                                <Typography variant="caption" color="text.secondary">
                                                    Reviewed by {checklist.reviewedBy.name}
                                                </Typography>
                                            )}
                                            
                                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    startIcon={<Visibility />}
                                                    onClick={() => handleViewDetails(checklist)}
                                                    sx={{ flex: '1 1 auto' }}
                                                >
                                                    View Details
                                                </Button>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    color="primary"
                                                    startIcon={<Refresh />}
                                                    onClick={() => {
                                                        window.dispatchEvent(new CustomEvent('weeklyChecklist:edit', { detail: checklist }));
                                                        const el = document.getElementById('weekly-form');
                                                        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                                    }}
                                                    sx={{ flex: '1 1 auto' }}
                                                >
                                                    Edit / Resubmit
                                                </Button>
                                                {isAdmin && !checklist.reviewedBy && (
                                                    <Button
                                                        size="small"
                                                        variant="contained"
                                                        color="success"
                                                        startIcon={<CheckCircle />}
                                                        onClick={() => handleMarkReviewed(checklist)}
                                                        disabled={submitting}
                                                        sx={{ flex: '1 1 auto' }}
                                                    >
                                                        Mark Reviewed
                                                    </Button>
                                                )}
                                            </Stack>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </Stack>
                </Box>

                {/* Desktop table view */}
				<TableContainer sx={{ display: { xs: 'none', md: 'block' }, maxHeight: 600 }}>
                    {console.log('[WeeklyChecklistList] RENDERING DESKTOP TABLE VIEW', { count: filteredAndSorted.length, screenWidth })}
                    <Table>
					<TableHead sx={{ position: 'sticky', top: 0, backgroundColor: 'background.paper', zIndex: 1 }}>
						<TableRow>
							<TableCell sortDirection={orderBy === 'weekStartDate' ? order : false}>
								<TableSortLabel
									active={orderBy === 'weekStartDate'}
									direction={orderBy === 'weekStartDate' ? order : 'asc'}
									onClick={() => handleRequestSort('weekStartDate')}
								>
									Date Range
								</TableSortLabel>
							</TableCell>
							<TableCell sortDirection={orderBy === 'truck' ? order : false}>
								<TableSortLabel
									active={orderBy === 'truck'}
									direction={orderBy === 'truck' ? order : 'asc'}
									onClick={() => handleRequestSort('truck')}
								>
									Truck
								</TableSortLabel>
							</TableCell>
							<TableCell sortDirection={orderBy === 'driver' ? order : false}>
								<TableSortLabel
									active={orderBy === 'driver'}
									direction={orderBy === 'driver' ? order : 'asc'}
									onClick={() => handleRequestSort('driver')}
								>
									Driver
								</TableSortLabel>
							</TableCell>
							<TableCell sortDirection={orderBy === 'miles' ? order : false}>
								<TableSortLabel
									active={orderBy === 'miles'}
									direction={orderBy === 'miles' ? order : 'asc'}
									onClick={() => handleRequestSort('miles')}
								>
									Miles
								</TableSortLabel>
							</TableCell>
                            {/* Status and Issues columns removed per request */}
							<TableCell sortDirection={orderBy === 'submittedAt' ? order : false}>
								<TableSortLabel
									active={orderBy === 'submittedAt'}
									direction={orderBy === 'submittedAt' ? order : 'asc'}
									onClick={() => handleRequestSort('submittedAt')}
								>
									Submitted
								</TableSortLabel>
							</TableCell>
							<TableCell sortDirection={orderBy === 'reviewed' ? order : false}>
								<TableSortLabel
									active={orderBy === 'reviewed'}
									direction={orderBy === 'reviewed' ? order : 'asc'}
									onClick={() => handleRequestSort('reviewed')}
								>
									Reviewed
								</TableSortLabel>
							</TableCell>
							<TableCell align="center">Actions</TableCell>
						</TableRow>
					</TableHead>
                        <TableBody>
						{filteredAndSorted.map((checklist, idx) => {
                                const issuesCount = getIssuesCount(checklist);
                                const milesDriven = formatTotalWeeklyMiles(checklist);
                                
                                return (
								<TableRow 
                                        key={checklist._id}
                                        sx={{ 
										'&:hover': { backgroundColor: 'action.hover' },
										'&:nth-of-type(odd)': { backgroundColor: 'grey.50' },
										height: 64
                                        }}
                                    >
                                        <TableCell>
                                            <Stack>
                                                <Typography variant="body2" fontWeight="medium">
                                                    {formatDate(checklist.weekStartDate)}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    to {formatDate(checklist.weekEndDate)}
                                                </Typography>
                                            </Stack>
                                        </TableCell>
                                        
                                        <TableCell>
                                            <Stack>
                                                <Typography variant="body2" fontWeight="medium">
                                                    {getTruckLabel(checklist.truck)}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {checklist.truck?.make} {checklist.truck?.model}
                                                </Typography>
                                            </Stack>
                                        </TableCell>
                                        
                                        <TableCell>
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                <Person fontSize="small" color="action" />
                                                <Typography variant="body2">
                                                    {checklist.submittedBy?.name || 'Unknown'}
                                                </Typography>
                                            </Stack>
                                        </TableCell>
                                        
                                        <TableCell>
                                            <Stack>
                                                <Typography variant="body2" fontWeight="medium">
                                                    {milesDriven} mi
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    ${checklist.fuelPurchases?.toFixed(2) || '0.00'} fuel
                                                </Typography>
                                            </Stack>
                                        </TableCell>
                                        
                                        {/* Status and Issues cells removed */}
                                        
                                        <TableCell>
                                            <Stack>
                                                <Typography variant="body2" fontWeight="medium">
                                                    {checklist.submittedAt ? new Date(checklist.submittedAt).toLocaleString() : '—'}
                                                </Typography>
                                            </Stack>
                                        </TableCell>
                                        
                                        <TableCell>
                                            {checklist.reviewedBy ? (
                                                <Stack>
                                                    <Chip
                                                        label="Reviewed"
                                                        color="success"
                                                        size="small"
                                                        icon={<CheckCircle />}
                                                    />
                                                    <Typography variant="caption" color="text.secondary">
                                                        by {checklist.reviewedBy.name}
                                                    </Typography>
                                                </Stack>
                                            ) : (
									<Chip
										label="Pending Review"
										color="warning"
										size="small"
									/>
                                            )}
                                        </TableCell>
                                        
                                        <TableCell align="center">
                                            <Stack direction="row" spacing={1} justifyContent="center">
                                                <Tooltip title="View Details">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleViewDetails(checklist)}
                                                    >
                                                        <Visibility />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Edit / Resubmit">
                                                    <IconButton
                                                        size="small"
                                                        color="primary"
                                                        onClick={() => {
                                                            // Dispatch a custom event for form to load this checklist
                                                            window.dispatchEvent(new CustomEvent('weeklyChecklist:edit', { detail: checklist }));
                                                            // Scroll to form
                                                            const el = document.getElementById('weekly-form');
                                                            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                                        }}
                                                    >
                                                        <Refresh />
                                                    </IconButton>
                                                </Tooltip>
                                                
                                                {isAdmin && !checklist.reviewedBy && (
                                                    <Tooltip title="Mark as Reviewed">
                                                        <IconButton
                                                            size="small"
                                                            color="success"
                                                            onClick={() => handleMarkReviewed(checklist)}
                                                            disabled={submitting}
                                                        >
                                                            <CheckCircle />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Summary Row */}
                {checklists.length > 0 && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={3}>
                                <Typography variant="caption" color="text.secondary">
                                    Total Checklists
                                </Typography>
                                <Typography variant="h6">
                                    {checklists.length}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <Typography variant="caption" color="text.secondary">
                                    Total Miles
                                </Typography>
                                <Typography variant="h6">
                                    {checklists.reduce((sum, c) => sum + getTotalWeeklyMiles(c), 0).toFixed(1)}
                                </Typography>
                            </Grid>
                            {/* Removed Issues/Pending summary per request */}
                        </Grid>
                    </Box>
                )}
            </Paper>

            {/* Details Dialog */}
            <Dialog open={detailsDialogOpen} onClose={() => setDetailsDialogOpen(false)} maxWidth="lg" fullWidth>
                <DialogTitle>
                    {selectedChecklist ? `Weekly Checklist • ${formatDate(selectedChecklist.weekStartDate)} - ${formatDate(selectedChecklist.weekEndDate)}` : 'Weekly Checklist Details'}
                </DialogTitle>
                <DialogContent>
                    {selectedChecklist && (
                        <Box>
                            <Grid container spacing={3}>
                                {/* Basic Information */}
                                <Grid item xs={12} md={6}>
                                    <Typography variant="h6" gutterBottom>Basic Information</Typography>
                                    <Stack spacing={2}>
                                        <Box>
                                            <Typography variant="subtitle2">Driver:</Typography>
                                            <Typography variant="body2">{selectedChecklist.submittedBy?.name}</Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="subtitle2">Truck:</Typography>
                                            <Typography variant="body2">
                                                {selectedChecklist.truck?.truckNumber || selectedChecklist.truck?.unitNumber || '—'}
                                                {selectedChecklist.truck?.make || selectedChecklist.truck?.model || selectedChecklist.truck?.year ? (
                                                    <>
                                                        {` ${selectedChecklist.truck?.make || ''} ${selectedChecklist.truck?.model || ''}`.trim()}
                                                        {selectedChecklist.truck?.year ? ` (${selectedChecklist.truck.year})` : ''}
                                                    </>
                                                ) : null}
                                            </Typography>
                                        </Box>
                                        {selectedChecklist.trailer && (selectedChecklist.trailer.truckNumber || selectedChecklist.trailer.unitNumber || selectedChecklist.trailer.make || selectedChecklist.trailer.model || selectedChecklist.trailer.year) && (
                                            <Box>
                                                <Typography variant="subtitle2">Trailer:</Typography>
                                                <Typography variant="body2">
                                                    {selectedChecklist.trailer?.truckNumber || selectedChecklist.trailer?.unitNumber || '—'}
                                                    {selectedChecklist.trailer?.make || selectedChecklist.trailer?.model || selectedChecklist.trailer?.year ? (
                                                        <>
                                                            {` ${selectedChecklist.trailer?.make || ''} ${selectedChecklist.trailer?.model || ''}`.trim()}
                                                            {selectedChecklist.trailer?.year ? ` (${selectedChecklist.trailer.year})` : ''}
                                                        </>
                                                    ) : null}
                                                </Typography>
                                            </Box>
                                        )}
                                        <Box>
                                            <Typography variant="subtitle2">Week Period:</Typography>
                                            <Typography variant="body2">
                                                {formatDate(selectedChecklist.weekStartDate)} - {formatDate(selectedChecklist.weekEndDate)}
                                            </Typography>
                                        </Box>
                                        {/* Overall status and submitted date removed to match current form */}
                                        {selectedChecklist.reviewedBy && (
                                            <Box>
                                                <Typography variant="subtitle2">Reviewed By:</Typography>
                                                <Typography variant="body2">
                                                    {selectedChecklist.reviewedBy.name} on {formatDate(selectedChecklist.reviewedAt)}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Stack>
                                </Grid>
                                
                                {/* Operational Data */}
                                <Grid item xs={12} md={6}>
                                    <Typography variant="h6" gutterBottom>Operational Data</Typography>
                                    <Grid container spacing={1.5}>
                                        {Array.isArray(selectedChecklist.truckMileages) && selectedChecklist.truckMileages.length > 0 ? (
                                            <>
                                                {selectedChecklist.truckMileages.map((row, idx) => {
                                                    const label = mileageRowTruckLabel(row, idx, selectedChecklist);
                                                    const leg = Number(row.mileageEnd) - Number(row.mileageStart);
                                                    return (
                                                        <Grid item xs={12} key={idx}>
                                                            <Paper sx={{ p: 1.5 }}>
                                                                <Typography variant="caption" color="text.secondary">{label}</Typography>
                                                                <Typography variant="body2" fontWeight={600}>
                                                                    {row.mileageStart ?? '—'} → {row.mileageEnd ?? '—'}
                                                                    {Number.isFinite(leg) ? ` (${leg.toFixed(1)} mi)` : ''}
                                                                </Typography>
                                                            </Paper>
                                                        </Grid>
                                                    );
                                                })}
                                                <Grid item xs={12} sm={6} md={4}>
                                                    <Paper sx={{ p: 1.5 }}>
                                                        <Typography variant="caption" color="text.secondary">Total miles (all trucks)</Typography>
                                                        <Typography variant="body1" fontWeight={700}>{formatTotalWeeklyMiles(selectedChecklist)} mi</Typography>
                                                    </Paper>
                                                </Grid>
                                            </>
                                        ) : (
                                            <>
                                                <Grid item xs={12} sm={6} md={4}>
                                                    <Paper sx={{ p: 1.5 }}>
                                                        <Typography variant="caption" color="text.secondary">Start Odometer</Typography>
                                                        <Typography variant="body1" fontWeight={700}>{selectedChecklist.mileageStart ?? '—'}</Typography>
                                                    </Paper>
                                                </Grid>
                                                <Grid item xs={12} sm={6} md={4}>
                                                    <Paper sx={{ p: 1.5 }}>
                                                        <Typography variant="caption" color="text.secondary">End Odometer</Typography>
                                                        <Typography variant="body1" fontWeight={700}>{selectedChecklist.mileageEnd ?? '—'}</Typography>
                                                    </Paper>
                                                </Grid>
                                                <Grid item xs={12} sm={6} md={4}>
                                                    <Paper sx={{ p: 1.5 }}>
                                                        <Typography variant="caption" color="text.secondary">Miles Driven</Typography>
                                                        <Typography variant="body1" fontWeight={700}>{calculateMilesDriven(selectedChecklist.mileageStart, selectedChecklist.mileageEnd)} mi</Typography>
                                                    </Paper>
                                                </Grid>
                                            </>
                                        )}
                                        {/* Fuel Purchases removed per request */}
                                    </Grid>
                                </Grid>
                                
                                {/* Vehicle Inspection, Safety Equipment, Safety Compliance removed to match current form presentation */}
                                
                                {/* Operational Details (new) */}
                                {(selectedChecklist.oversizeLoadedMiles || selectedChecklist.dotInspectionCompleted || selectedChecklist.hasReimbursement || selectedChecklist.cashReceiptsAmount || (selectedChecklist.cashReceiptsAttachments && selectedChecklist.cashReceiptsAttachments.length) || selectedChecklist.hasBreakdownPay || (selectedChecklist.breakdownEvents && selectedChecklist.breakdownEvents.length) || (selectedChecklist.detentionEvents && selectedChecklist.detentionEvents.length) || selectedChecklist.weekendPay === 'half' || selectedChecklist.weekendPay === 'full' || (selectedChecklist.weekendDispatchDates && selectedChecklist.weekendDispatchDates.length) || (selectedChecklist.weeklyLoads && selectedChecklist.weeklyLoads.length)) && (
                                <Grid item xs={12}>
                                        <Typography variant="h6" gutterBottom>Operational Details</Typography>
                                        <Grid container spacing={1.5}>
                                            {typeof selectedChecklist.oversizeLoadedMiles === 'number' && selectedChecklist.oversizeLoadedMiles > 0 && (
                                                <Grid item xs={12} sm={6} md={3}>
                                                    <Paper sx={{ p: 1.5 }}>
                                                        <Typography variant="caption" color="text.secondary">Loaded Oversize Miles</Typography>
                                                        <Typography variant="body1" fontWeight={700}>{selectedChecklist.oversizeLoadedMiles}</Typography>
                                                    </Paper>
                                            </Grid>
                                            )}
                                            {typeof selectedChecklist.dotInspectionCompleted === 'boolean' && (
                                                <Grid item xs={12} sm={6} md={3}>
                                                    <Paper sx={{ p: 1.5 }}>
                                                        <Typography variant="caption" color="text.secondary">DOT Inspection</Typography>
                                                        <Typography variant="body1" fontWeight={700}>{selectedChecklist.dotInspectionCompleted ? 'Completed' : 'Not Completed'}</Typography>
                                                    </Paper>
                                    </Grid>
                                            )}
                                            {selectedChecklist.hasReimbursement && (
                                                <Grid item xs={12} sm={6} md={3}>
                                                    <Paper sx={{ p: 1.5 }}>
                                                        <Typography variant="caption" color="text.secondary">Reimbursements this week</Typography>
                                                        <Typography variant="body1" fontWeight={700}>Yes</Typography>
                                                    </Paper>
                                                </Grid>
                                            )}
                                            {(typeof selectedChecklist.cashReceiptsAmount === 'number' && selectedChecklist.cashReceiptsAmount > 0) && (
                                                <Grid item xs={12} sm={6} md={3}>
                                                    <Paper sx={{ p: 1.5 }}>
                                                        <Typography variant="caption" color="text.secondary">Cash Receipts / Reimbursement</Typography>
                                                        <Typography variant="body1" fontWeight={700}>${selectedChecklist.cashReceiptsAmount.toFixed(2)}</Typography>
                                                    </Paper>
                                </Grid>
                                            )}
                                            {selectedChecklist.cashReceiptsNote && (
                                <Grid item xs={12}>
                                                    <Paper sx={{ p: 1.5 }}>
                                                        <Typography variant="caption" color="text.secondary">Cash Receipts Note</Typography>
                                                        <Typography variant="body2">{selectedChecklist.cashReceiptsNote}</Typography>
                                                    </Paper>
                                                </Grid>
                                            )}
                                            {Array.isArray(selectedChecklist.cashReceiptsAttachments) && selectedChecklist.cashReceiptsAttachments.length > 0 && (
                                                <Grid item xs={12}>
                                                    <Typography variant="caption" color="text.secondary">Receipt photos</Typography>
                                                    <ReceiptAttachmentGallery
                                                        attachments={selectedChecklist.cashReceiptsAttachments}
                                                        ownerId={selectedChecklist._id}
                                                        kind="checklist"
                                                    />
                                                </Grid>
                                            )}
                                        </Grid>

                                        {(selectedChecklist.hasBreakdownPay || (Array.isArray(selectedChecklist.breakdownEvents) && selectedChecklist.breakdownEvents.length > 0)) && (
                                            <Box sx={{ mt: 2 }}>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Breakdown pay</Typography>
                                                <Grid container spacing={1.5} sx={{ mb: 1 }}>
                                                    <Grid item xs={12} sm={6} md={3}>
                                                        <Paper sx={{ p: 1.5 }}>
                                                            <Typography variant="caption" color="text.secondary">Breakdown pay this week</Typography>
                                                            <Typography variant="body1" fontWeight={700}>{selectedChecklist.hasBreakdownPay ? 'Yes' : 'No'}</Typography>
                                                        </Paper>
                                                    </Grid>
                                                </Grid>
                                                {Array.isArray(selectedChecklist.breakdownEvents) && selectedChecklist.breakdownEvents.length > 0 && (
                                                    <TableContainer component={Paper}>
                                                        <Table size="small">
                                                            <TableHead>
                                                                <TableRow>
                                                                    <TableCell>Date</TableCell>
                                                                    <TableCell>Time of Notification</TableCell>
                                                                </TableRow>
                                                            </TableHead>
                                                            <TableBody>
                                                                {selectedChecklist.breakdownEvents.map((ev, i) => (
                                                                    <TableRow key={i}>
                                                                        <TableCell>{ev?.date ? formatDate(ev.date) : '—'}</TableCell>
                                                                        <TableCell>{ev?.notificationTime || '—'}</TableCell>
                                                                    </TableRow>
                                                                ))}
                                                            </TableBody>
                                                        </Table>
                                                    </TableContainer>
                                                )}
                                            </Box>
                                        )}

                                        {/* Detention Events */}
                                        {Array.isArray(selectedChecklist.detentionEvents) && selectedChecklist.detentionEvents.length > 0 && (
                                            <Box sx={{ mt: 2 }}>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Detention</Typography>
                                                <TableContainer component={Paper}>
                                                    <Table size="small">
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableCell>Date</TableCell>
                                                                <TableCell>Location</TableCell>
                                                                <TableCell>Arrival</TableCell>
                                                                <TableCell>Depart</TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {selectedChecklist.detentionEvents.map((ev, i) => (
                                                                <TableRow key={i}>
                                                                    <TableCell>{ev?.date ? formatDate(ev.date) : '—'}</TableCell>
                                                                    <TableCell>{ev?.location || '—'}</TableCell>
                                                                    <TableCell>{ev?.arrivalTime || '—'}</TableCell>
                                                                    <TableCell>{ev?.departTime || '—'}</TableCell>
                                                                </TableRow>
                                                            ))}
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                            </Box>
                                        )}

                                        {(selectedChecklist.weekendPay === 'half' || selectedChecklist.weekendPay === 'full') && (
                                            <Box sx={{ mt: 2 }}>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Weekend Pay</Typography>
                                                <Paper sx={{ p: 1.5, display: 'inline-block' }}>
                                                    <Typography variant="body1" fontWeight={700}>
                                                        {selectedChecklist.weekendPay === 'half' ? 'Half Weekend' : 'Full Weekend'}
                                                    </Typography>
                                                </Paper>
                                            </Box>
                                        )}

                                        {Array.isArray(selectedChecklist.weekendDispatchDates) && selectedChecklist.weekendDispatchDates.length > 0 && selectedChecklist.weekendPay !== 'half' && selectedChecklist.weekendPay !== 'full' && (
                                            <Box sx={{ mt: 2 }}>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Weekend Dispatch</Typography>
                                                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                                                    {selectedChecklist.weekendDispatchDates.map((d, i) => (
                                                        <Chip key={i} label={d ? formatDate(d) : '—'} size="small" />
                                                    ))}
                                                </Stack>
                                            </Box>
                                        )}

                                        {/* Weekly Loads */}
                                        {Array.isArray(selectedChecklist.weeklyLoads) && selectedChecklist.weeklyLoads.length > 0 && (
                                            <Box sx={{ mt: 2 }}>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Weekly Loads</Typography>
                                                <TableContainer component={Paper}>
                                                    <Table size="small">
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableCell>Date</TableCell>
                                                                <TableCell>ELD Order #</TableCell>
                                                                <TableCell>Pickup City/State</TableCell>
                                                                <TableCell>Delivery City/State</TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {selectedChecklist.weeklyLoads.map((row, i) => (
                                                                <TableRow key={i}>
                                                                    <TableCell>{row?.date ? formatDate(row.date) : '—'}</TableCell>
                                                                    <TableCell>{row?.eldOrderNumber || '—'}</TableCell>
                                                                    <TableCell>{row?.from || '—'}</TableCell>
                                                                    <TableCell>{row?.to || '—'}</TableCell>
                                                                </TableRow>
                                                            ))}
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                            </Box>
                                        )}
                                </Grid>
                                )}
                                
                                {/* Maintenance Issues */}
                                {selectedChecklist.maintenanceIssues && (
                                    <Grid item xs={12}>
                                        <Typography variant="h6" gutterBottom>Maintenance Issues</Typography>
                                        <Paper sx={{ p: 2, bgcolor: 'grey.100' }}>
                                            <Typography variant="body2">{selectedChecklist.maintenanceIssues}</Typography>
                                        </Paper>
                                    </Grid>
                                )}
                                
                                {/* Weekly Metrics */}
                                {selectedChecklist.dailyMetrics && (
                                    <Grid item xs={12}>
                                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                            <Typography variant="h6">Weekly Metrics</Typography>
                                            <Typography variant="body2" color="error" fontWeight={700} sx={{ color: (theme) => `${theme.palette.error.main} !important` }}>
                                                * Entries in the table below must be recorded accurately on this checklist. Unrecorded items will not be reflected on paychecks.
                                            </Typography>
                                        </Stack>
                                        <TableContainer component={Paper}>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Metric</TableCell>
                                                        {['Sun','Mon','Tues','Wed','Thurs','Fri','Sat'].map((d) => (
                                                            <TableCell key={d} align="center">{d}</TableCell>
                                                        ))}
                                                        <TableCell align="center">Total</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {/* Nights Out */}
                                                    <TableRow>
                                                        <TableCell>Nights Out</TableCell>
                                                        {(selectedChecklist.dailyMetrics.nightsOut || Array(7).fill(false)).map((v, i) => (
                                                            <TableCell key={i} align="center">{v ? '✓' : '-'}</TableCell>
                                                        ))}
                                                        <TableCell align="center"><strong>{(selectedChecklist.dailyMetrics.nightsOut || []).filter(Boolean).length}</strong></TableCell>
                                                    </TableRow>
                                                    {/* Tarps */}
                                                    <TableRow>
                                                        <TableCell>Tarps</TableCell>
                                                        {(selectedChecklist.dailyMetrics.tarps || Array(7).fill('')).map((v, i) => (
                                                            <TableCell key={i} align="center">{formatTarpCell(v)}</TableCell>
                                                        ))}
                                                        <TableCell align="center"><strong>{formatTarpTotal(sumTarps(selectedChecklist.dailyMetrics.tarps))}</strong></TableCell>
                                                    </TableRow>
                                                    {/* Extra drop/pickup */}
                                                    <TableRow>
                                                        <TableCell>
                                                            <Stack spacing={0}>
                                                                <span>Extra drop/pickup</span>
                                                                <Typography variant="caption" color="text.secondary">*Additional Docks for TG Lanes</Typography>
                                                            </Stack>
                                                        </TableCell>
                                                        {(selectedChecklist.dailyMetrics.extraDropPickup || Array(7).fill('')).map((v, i) => (
                                                            <TableCell key={i} align="center">{v || '-'}</TableCell>
                                                        ))}
                                                        <TableCell align="center"><strong>{(selectedChecklist.dailyMetrics.extraDropPickup || []).reduce((t, v) => t + (parseInt(v, 10) || 0), 0)}</strong></TableCell>
                                                    </TableRow>
                                                    {/* WW Wood Cabinet Drops */}
                                                    <TableRow>
                                                        <TableCell>WW Wood Cabinet Drops</TableCell>
                                                        {(selectedChecklist.dailyMetrics.wwWoodCabinetDrops || Array(7).fill('')).map((v, i) => (
                                                            <TableCell key={i} align="center">{v || '-'}</TableCell>
                                                        ))}
                                                        <TableCell align="center"><strong>{(selectedChecklist.dailyMetrics.wwWoodCabinetDrops || []).reduce((t, v) => t + (parseInt(v, 10) || 0), 0)}</strong></TableCell>
                                                    </TableRow>
                                                    {/* Pre-Load (Not Delivering) */}
                                                    <TableRow>
                                                        <TableCell>
                                                            <Stack spacing={0}>
                                                                <span>Pre-Load (Not Delivering)</span>
                                                                <Typography variant="caption" color="text.secondary">*Pickup & drop within 160 miles of yard; trailer moves lot-to-lot</Typography>
                                                            </Stack>
                                                        </TableCell>
                                                        {(selectedChecklist.dailyMetrics.preLoadNotDelivering || Array(7).fill('')).map((v, i) => (
                                                            <TableCell key={i} align="center">{v || '-'}</TableCell>
                                                        ))}
                                                        <TableCell align="center"><strong>{(selectedChecklist.dailyMetrics.preLoadNotDelivering || []).reduce((t, v) => t + (parseInt(v, 10) || 0), 0)}</strong></TableCell>
                                                        </TableRow>
                                                    {/* Oversize/Truss */}
                                                    <TableRow>
                                                        <TableCell>
                                                            <Stack spacing={0}>
                                                                <span>Oversize/Truss</span>
                                                                <Typography variant="caption" color="text.secondary">*with permits</Typography>
                                                            </Stack>
                                                        </TableCell>
                                                        {(selectedChecklist.dailyMetrics.oversizeTruss || Array(7).fill(false)).map((v, i) => (
                                                            <TableCell key={i} align="center">{v ? '✓' : '-'}</TableCell>
                                                        ))}
                                                        <TableCell align="center"><strong>{(selectedChecklist.dailyMetrics.oversizeTruss || []).filter(Boolean).length}</strong></TableCell>
                                                    </TableRow>
                                                    {/* Short Run (Delivering) */}
                                                    <TableRow>
                                                        <TableCell>
                                                            <Stack spacing={0}>
                                                                <span>Short Run (Delivering)</span>
                                                                <Typography variant="caption" color="text.secondary">*Pickup & delivery within 325 miles</Typography>
                                                            </Stack>
                                                        </TableCell>
                                                        {Array(7).fill(0).map((_, i) => {
                                                            const yesNo = (selectedChecklist.dailyMetrics.shortRunYes || [])[i];
                                                            const orders = (selectedChecklist.dailyMetrics.shortRunOrders || [])[i] || [];
                                                            const legacy = (selectedChecklist.dailyMetrics.shortRunDelivering || [])[i];
                                                            const cell = yesNo ? (yesNo === 'yes' ? (orders.length ? orders.join(', ') : 'Yes') : 'No') : (legacy ? (parseInt(legacy, 10) > 0 ? 'Yes' : 'No') : '—');
                                                            return (<TableCell key={i} align="center">{cell}</TableCell>);
                                                        })}
                                                        <TableCell align="center"><strong>{(selectedChecklist.dailyMetrics.shortRunOrders || []).reduce((t, arr) => t + (Array.isArray(arr) ? arr.filter(o => /\d+/.test(String(o))).length : 0), 0)}</strong></TableCell>
                                                    </TableRow>
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Grid>
                                )}

                                {/* Comments / Notes */}
                                {selectedChecklist.additionalNotes && (
                                    <Grid item xs={12}>
                                        <Typography variant="h6" gutterBottom>Comments / Notes</Typography>
                                        <Paper sx={{ p: 2, bgcolor: 'grey.100' }}>
                                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{selectedChecklist.additionalNotes}</Typography>
                                        </Paper>
                                    </Grid>
                                )}
                            </Grid>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    {isAdmin && selectedChecklist && !selectedChecklist.reviewedBy && (
                        <Button
                            onClick={() => {
                                handleMarkReviewed(selectedChecklist);
                                setDetailsDialogOpen(false);
                            }}
                            variant="contained"
                            startIcon={<CheckCircle />}
                            disabled={submitting}
                        >
                            {submitting ? 'Processing...' : 'Mark as Reviewed'}
                        </Button>
                    )}
                    <Button startIcon={<Print />} onClick={() => setPrintOpen(true)}>
                        Print
                    </Button>
                    <Button onClick={() => setDetailsDialogOpen(false)}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
            <WeeklyChecklistPrintDialog
                open={printOpen}
                onClose={() => setPrintOpen(false)}
                checklists={checklists}
                initialChecklist={selectedChecklist}
            />
        </Box>
    );
};

export default WeeklyChecklistList; 